import * as SheetPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import * as React from "react";

import { cn } from "shadcn/lib/utils";

const SheetContext = React.createContext<{
  open: boolean;
  onExitComplete: () => void;
}>({ open: false, onExitComplete: () => {} });

function Sheet({ open, ...props }: React.ComponentProps<typeof SheetPrimitive.Root>) {
  const [dialogOpen, setDialogOpen] = React.useState(open ?? false);
  const openRef = React.useRef(open ?? false);
  openRef.current = open ?? false;

  React.useEffect(() => {
    if (open) setDialogOpen(true);
  }, [open]);

  const handleExitComplete = React.useCallback(() => {
    if (!openRef.current) setDialogOpen(false);
  }, []);

  return (
    <AnimatePresence mode="wait">
      {dialogOpen ? (
        <SheetContext.Provider value={{ open: open ?? false, onExitComplete: handleExitComplete }}>
          <SheetPrimitive.Root open={dialogOpen} {...props} />
        </SheetContext.Provider>
      ) : null}
    </AnimatePresence>
  );
}

const SheetTrigger = SheetPrimitive.Trigger;

const SheetClose = SheetPrimitive.Close;

const SheetPortal = SheetPrimitive.Portal;

const SheetOverlay = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Overlay
    className={cn("fixed inset-0 z-50 bg-black/80", className)}
    {...props}
    ref={ref}
  />
));
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName;

const sheetVariants = cva("fixed z-50 bg-background p-6 shadow-lg", {
  variants: {
    side: {
      top: "inset-x-0 top-0 border-b",
      bottom: "inset-x-0 bottom-0 border-t",
      left: "inset-y-0 left-0 h-full w-3/4 border-r sm:max-w-sm",
      right: "inset-y-0 right-0 h-full w-3/4 border-l sm:max-w-sm",
    },
  },
  defaultVariants: {
    side: "right",
  },
});

const slideVariants = {
  right: { hidden: { x: "100%" }, visible: { x: 0 } },
  left: { hidden: { x: "-100%" }, visible: { x: 0 } },
  top: { hidden: { y: "-100%" }, visible: { y: 0 } },
  bottom: { hidden: { y: "100%" }, visible: { y: 0 } },
};

interface SheetContentProps
  extends
    React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>,
    VariantProps<typeof sheetVariants> {}

const SheetContent = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Content>,
  SheetContentProps
>(({ side = "right", className, children, ...props }, ref) => {
  const { open, onExitComplete } = React.useContext(SheetContext);
  const slide = slideVariants[side ?? "right"];

  return (
    <SheetPortal forceMount>
      <SheetPrimitive.Overlay asChild forceMount>
        <motion.div
          animate={{ opacity: open ? 1 : 0 }}
          className="fixed inset-0 z-50 bg-black/80"
          initial={{ opacity: 0 }}
          style={{ pointerEvents: open ? "auto" : "none" }}
          transition={{ duration: 0.3, ease: "easeInOut" }}
        />
      </SheetPrimitive.Overlay>
      <SheetPrimitive.Content asChild forceMount ref={ref} {...props}>
        <motion.div
          animate={open ? slide.visible : slide.hidden}
          className={cn(sheetVariants({ side }), className)}
          initial={slide.hidden}
          transition={{ type: "spring", damping: 30, stiffness: 300 }}
          onAnimationComplete={() => {
            if (!open) onExitComplete();
          }}
        >
          <SheetPrimitive.Close className="ring-offset-background absolute top-4 right-4 rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:outline-hidden disabled:pointer-events-none">
            <X className="h-4 w-4" />
            <span className="sr-only">Close</span>
          </SheetPrimitive.Close>
          {children}
        </motion.div>
      </SheetPrimitive.Content>
    </SheetPortal>
  );
});
SheetContent.displayName = SheetPrimitive.Content.displayName;

const SheetHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col text-center sm:text-left", className)} {...props} />
);
SheetHeader.displayName = "SheetHeader";

const SheetFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props}
  />
);
SheetFooter.displayName = "SheetFooter";

const SheetTitle = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Title
    className={cn("text-title text-foreground", className)}
    ref={ref}
    {...props}
  />
));
SheetTitle.displayName = SheetPrimitive.Title.displayName;

const SheetDescription = React.forwardRef<
  React.ElementRef<typeof SheetPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
  <SheetPrimitive.Description
    className={cn("text-body2 text-muted-foreground", className)}
    ref={ref}
    {...props}
  />
));
SheetDescription.displayName = SheetPrimitive.Description.displayName;

export {
  Sheet,
  SheetPortal,
  SheetOverlay,
  SheetTrigger,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetDescription,
};
